import React, { useState, useEffect } from 'react';
import { User } from '../types';
import { getProfile, updateProfile, uploadAvatar } from '../api/client';
import {
  User as UserIcon,
  Smile,
  UserCircle,
  Heart,
  Star,
  Coffee,
  Briefcase,
  GraduationCap,
  Shield,
  Crown
} from 'lucide-react';
import Navbar from '../components/Navbar';
import './Profile.css';

const AVATAR_OPTIONS = [
  { icon: UserIcon, name: 'User' },
  { icon: Smile, name: 'Smile' },
  { icon: UserCircle, name: 'UserCircle' },
  { icon: Heart, name: 'Heart' },
  { icon: Star, name: 'Star' },
  { icon: Coffee, name: 'Coffee' },
  { icon: Briefcase, name: 'Briefcase' },
  { icon: GraduationCap, name: 'GraduationCap' },
  { icon: Shield, name: 'Shield' },
  { icon: Crown, name: 'Crown' },
];

const ICON_MAP: Record<string, React.FC<{ size?: number; color?: string }>> = {
  'User': UserIcon,
  'Smile': Smile,
  'UserCircle': UserCircle,
  'Heart': Heart,
  'Star': Star,
  'Coffee': Coffee,
  'Briefcase': Briefcase,
  'GraduationCap': GraduationCap,
  'Shield': Shield,
  'Crown': Crown,
};

const AVATAR_COLORS = [
  '#4F46E5', // indigo
  '#7C3AED', // violet
  '#EC4899', // pink
  '#EF4444', // red
  '#F97316', // orange
  '#EAB308', // yellow
  '#22C55E', // green
  '#14B8A6', // teal
  '#3B82F6', // blue
  '#6B7280', // gray
];

const Profile: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);
  const [avatarColor, setAvatarColor] = useState('#4F46E5');

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const profile = await getProfile();
        setUser(profile);
        setName(profile.name || '');
        setEmail(profile.email || '');
        setPhone(profile.phone || '');
        if (profile.avatar_color) {
          setAvatarColor(profile.avatar_color);
        }
      } catch (err) {
        setError('Ошибка загрузки профиля');
      } finally {
        setLoading(false);
      }
    };
    loadProfile();
  }, []);

  const handleSaveProfile = async () => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const updated = await updateProfile({ name, phone, avatar_color: avatarColor });
      setUser(updated);
      localStorage.setItem('user', JSON.stringify({ ...JSON.parse(localStorage.getItem('user') || '{}'), ...updated }));
      setMessage('Профиль обновлён');
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarSelect = async (iconName: string) => {
    if (!user) return;
    setShowAvatarPicker(false);
    const iconUrl = `/api/users/avatar?icon=${iconName}`;
    const updatedUser = { ...user, avatar_url: iconUrl };
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify({ ...JSON.parse(localStorage.getItem('user') || '{}'), avatar_url: iconUrl }));
    await updateProfile({ avatar_url: iconUrl, avatar_color: avatarColor });
    setMessage('Аватарка обновлена');
  };

  const handleColorSelect = async (color: string) => {
    if (!user) return;
    setAvatarColor(color);
    const updatedUser = { ...user, avatar_color: color };
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify({ ...JSON.parse(localStorage.getItem('user') || '{}'), avatar_color: color }));
    await updateProfile({ avatar_color: color });
    setMessage('Цвет аватарки обновлён');
  };

  if (loading) {
    return (
      <div className="profile-loading">
        <div className="spinner"></div>
        <p>Загрузка...</p>
      </div>
    );
  }

  if (!user) return null;

  const avatarUrl = user.avatar_url
    ? `${process.env.REACT_APP_API_URL || 'http://localhost:3010'}${user.avatar_url}`
    : null;
  const isIconAvatar = user.avatar_url?.includes('icon=');
  const iconName = isIconAvatar ? user.avatar_url?.split('icon=')[1] : null;
  const IconComponent = iconName ? ICON_MAP[iconName] : null;

  return (
    <div className="profile-page">
      <Navbar user={user} />
      <main className="profile-main">
        <h1>Профиль</h1>

        {message && <div className="message success">{message}</div>}
        {error && <div className="message error">{error}</div>}

        <div className="profile-avatar-section">
          <div className="profile-avatar-wrapper">
            {avatarUrl && !isIconAvatar ? (
              <img src={avatarUrl} alt={user.name} className="profile-avatar" />
            ) : IconComponent ? (
              <div className="profile-avatar-icon" style={{ background: avatarColor }}>
                <IconComponent size={36} color="white" />
              </div>
            ) : (
              <div className="profile-avatar-placeholder" style={{ background: avatarColor }}>
                {user.name.charAt(0).toUpperCase()}
              </div>
            )}
          </div>
          <div className="avatar-actions">
            <button className="avatar-upload-btn" onClick={() => setShowAvatarPicker(!showAvatarPicker)}>
              Выбрать аватарку
            </button>
          </div>
        </div>

        {showAvatarPicker && (
          <div className="avatar-picker">
            <p>Выберите иконку:</p>
            <div className="avatar-picker-grid">
              {AVATAR_OPTIONS.map((option) => {
                const IconComponent = option.icon;
                return (
                  <button
                    key={option.name}
                    className="avatar-picker-item"
                    onClick={() => handleAvatarSelect(option.name)}
                    title={option.name}
                  >
                    <IconComponent size={32} />
                  </button>
                );
              })}
            </div>
            <p className="avatar-picker-colors-title">Выберите цвет:</p>
            <div className="avatar-picker-colors">
              {AVATAR_COLORS.map((color) => (
                <button
                  key={color}
                  className={`avatar-color-item ${avatarColor === color ? 'avatar-color-item--selected' : ''}`}
                  style={{ background: color }}
                  onClick={() => handleColorSelect(color)}
                  title={color}
                />
              ))}
            </div>
          </div>
        )}

        <div className="profile-form">
          <div className="form-group">
            <label>ФИО Сотрудника</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="form-input"
            />
          </div>
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              value={email}
              readOnly
              className="form-input form-input--readonly"
              title="Email нельзя изменить"
            />
          </div>
          <div className="form-group">
            <label>Телефон</label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="form-input"
            />
          </div>
          <button className="save-btn" onClick={handleSaveProfile} disabled={saving}>
            {saving ? 'Сохранение...' : 'Сохранить'}
          </button>
        </div>
      </main>
    </div>
  );
};

export default Profile;
